<template>
  <SelectorPanel
    type="alerts"
    text="Alerts"
    @open="changeDialog"
    :disabled="disabled"
    :force-close="forceClose"
  >
    <SelectorPanelHeader
      type="alerts"
      title="Alerts"
      @click-close="handleClickClose"
    ></SelectorPanelHeader>
    <v-container
      class="alerts-container"
      :style="{ width: dialogWidth, 'min-width': dialogMinWidth }"
    >
      <AlertManagement
        v-if="!displayWizard && isOpen"
        @change="managementViewChange"
        @update="addAlertToMap"
      ></AlertManagement>
      <AlertWizard v-if="displayWizard"></AlertWizard>
    </v-container>
  </SelectorPanel>
</template>

<script>
import lodashFilter from "lodash/filter";
import lodashRemove from "lodash/remove";
import lodashFind from "lodash/find";
import { mapGetters } from "vuex";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";

import {
  MODULE_PREFIX_ALERTS,
  MODULE_PREFIX_ALERTS_CONTENT,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_SEARCH,
} from "@/store";
import {
  GET_DETAILS_SIDEBAR,
  GET_ALERTS_REFRESH_MAP,
  GET_USER_ALERTS,
  GET_PREFERENCES,
  SAVE_PREFERENCES,
  SAVE_MAPPED_ALERTS,
} from "@/store/actions.type";
import {
  MAIN_MAP_ID,
  MAP_ICON_COLORS,
  MAP_ICON_NAMES,
} from "@/common/constants/maps";

import { VIEW_DETAILS } from "@/common/constants/alert.js";
import { getSources } from "@/services/alerts/alerts.service.js";
import {
  addToMap as addAlertsToMap,
  removeFromMap as removeAlertsFromMap,
} from "@/services/events/events.service";
import { now, hoursAgo24 } from "@/common/date.utilities";
import { GET_USER_MAPPED_CONTENT } from "@/store/actions.type";
import { pageTrackerLogger } from "@/common/pageTracker";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

export default {
  name: "AlertsSelector",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader"),
    AlertManagement: () =>
      import("@/components/alerts/management/AlertManagement"),
    AlertWizard: () => import("@/components/alerts/wizard/AlertWizard"),
  },
  created() {
    this.getPrefs();
    this.getAlerts();
  },
  data() {
    return {
      viewDetails: VIEW_DETAILS,
      currentView: VIEW_DETAILS.myAlerts.id,
      mapped: [],
      isOpen: false,
      selectedClusterFeatures: null,
      forceClose: null,
    };
  },
  computed: {
    ...mapGetters({
      displayWizard: MODULE_PREFIX_ALERTS + "displayWizard",
      mappedContent: MODULE_PREFIX_ALERTS_CONTENT + "mappedContent",
      alerts: MODULE_PREFIX_ALERTS + "alerts",
      preferences: MODULE_PREFIX_USER + "preferences",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      maps: MODULE_PREFIX_MAP + "maps",
      mappedAlerts: MODULE_PREFIX_ALERTS + "mapped",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      refreshMap: MODULE_PREFIX_ALERTS_CONTENT + "refreshMap",
      searchDetails: MODULE_PREFIX_SEARCH + "searchDetails",
      searchType: MODULE_PREFIX_SEARCH + "type",
    }),
    dialogWidth() {
      switch (this.currentView) {
        case this.viewDetails.myAlerts.id:
          return this.viewDetails.myAlerts.size;
        case this.viewDetails.exploreContent.id:
          return this.viewDetails.exploreContent.size;
        case this.viewDetails.wizard.id:
          return this.viewDetails.wizard.size;
        default:
          return this.viewDetails.myAlerts.size;
      }
    },
    dialogMinWidth() {
      switch (this.currentView) {
        case this.viewDetails.myAlerts.id:
          return this.viewDetails.myAlerts.minSize;
        case this.viewDetails.exploreContent.id:
          return this.viewDetails.exploreContent.minSize;
        case this.viewDetails.wizard.id:
          return this.viewDetails.wizard.minSize;
        default:
          return this.viewDetails.myAlerts.minSize;
      }
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
  },
  methods: {
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    changeDialog(open) {
      this.isOpen = open;
      if (this.currentView === this.viewDetails.exploreContent.id) {
        let url = "/home";
        if (open) {
          url = `/alerts`;
          if (location.code) {
            url = `/alerts/${location.code}`;
          }
        }
        window.history.replaceState({}, "Hyperion", url);
      }
    },
    managementViewChange(view) {
      this.currentView = view;
    },
    async getAlerts() {
      await this.$store.dispatch(MODULE_PREFIX_ALERTS + GET_USER_ALERTS, {});
    },
    getMappedContent(params) {
      return this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_USER_MAPPED_CONTENT,
        params
      );
    },
    setDefaultPrefs() {
      this.preferences.alerts = {
        content: { alertFilter: [], sourceFilter: [] },
        mapped: [],
      };
      this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.preferences
      );
    },
    savePrefs() {
      this.preferences.alerts.mapped = this.mapped;
      return this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.preferences
      );
    },
    validateMappedPreferences(mappedAlerts) {
      const strValues = lodashFilter(mappedAlerts, (item) => {
        return typeof item === "string";
      });
      return strValues && strValues.length === 0;
    },
    async getPrefs() {
      await this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES, {});
      if (
        this.preferences &&
        this.preferences.alerts &&
        Array.isArray(this.preferences.alerts.mapped)
      ) {
        if (!this.validateMappedPreferences(this.preferences.alerts.mapped)) {
          console.log("RESETTING MAPPED ALERTS PREFERENCES");
          this.mapped = [];
          this.preferences.alerts.mapped = [];
        }
        this.getData({ alert: this.preferences.alerts.mapped });
        this.mapped = this.preferences.alerts.mapped;
      } else {
        this.setDefaultPrefs();
      }
      return await this.$store.dispatch(
        MODULE_PREFIX_ALERTS + SAVE_MAPPED_ALERTS,
        this.mapped
      );
    },
    async getData(input) {
      if (input.alert instanceof Array) {
        if (input.alert && input.alert.length === 0) {
          console.log("NO ALERTS TO MAP");
          return;
        }
      } else if (!input.alert.id) {
        return console.log("CANNOT MAP ALERT", input.alert);
      }

      try {
        const params = await this.buildQuery(input.alert, true);
        await this.getMappedContent(params);
        if (Array.isArray(input.alert) && input.alert.length > 0) {
          for (let i = 0; i < input.alert.length; i++) {
            this.addToMap(input.alert[i], "layer-" + input.alert[i].id);
          }
        } else if (input.alert && input.alert.id) {
          await this.addToMap(input.alert, "layer-" + input.alert.id);
        }
      } catch (err) {
        console.log("getData error", err);
      }
    },
    async buildQuery(alert, isMapping) {
      let params = {};
      params.geoOnly = true;
      params.pageOffset = 0;
      params.pageSize = isMapping ? 1000 : 100;
      params.start = hoursAgo24;
      params.end = now;
      params.sortDirection = "desc";
      if (alert.reduce) {
        params.alertsIdFilter = alert
          .reduce((accum, item) => {
            if (item.id) {
              accum.push(item.id);
            }
            return accum;
          }, [])
          .join(",");
      } else {
        params.alertsIdFilter = alert.id;
      }

      const SOURCES = getSources();
      params.sourceFilter = SOURCES.map((item) => item.id);
      return params;
    },
    addAlertToMap(input) {
      if (input.mapped) {
        this.getData(input);
        this.mapped.push(input.alert);
      } else {
        this.removeFromMap(input);
        lodashRemove(this.mapped, input.alert);
      }
      this.savePrefs();
    },
    addToMap(alert, layer) {
      if (!layer) {
        layer = this.layerId;
      }

      let res = this.mappedContent.data.filter((item) => {
        return item.properties.sourceAlert === alert.id;
      });

      const PARAMS = {
        data: res,
        layerId: layer,
        mapClickEvent: this.mapClickEvent,
        clusterMapClickEvent: this.clusterMapClickEvent,
        map: this.map,
        iconName: MAP_ICON_NAMES.alerts,
      };

      addAlertsToMap(PARAMS);
    },
    removeFromMap(input) {
      if (!input) {
        input = {};
      }
      if (!input.layerId) {
        input.layerId = this.layerId;
      }
      const PARAMS = {
        map: this.map,
        layerId: input.layerId,
      };
      removeAlertsFromMap(PARAMS);
    },
    displayResults(features) {
      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.mapClick;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.alerts;
      this.detailsSidebar.features = features || [];
      this.detailsSidebar.iconColor = MAP_ICON_COLORS.alerts;
      this.detailsSidebar.iconName = MAP_ICON_NAMES.alerts;
      if (features && Array.isArray(features) && features.length) {
        const ALERT = lodashFind(this.alerts, [
          "id",
          features[0].properties.sourceAlert,
        ]);
        let alertText = "Alerts";
        if (ALERT && ALERT.name) {
          alertText = ALERT.name;
          if (
            ALERT.name.toLowerCase().substr(ALERT.name.length - 5) !== "alert"
          ) {
            alertText = `${ALERT.name} Alert`;
          }
        }
        this.detailsSidebar.title = alertText;
      }

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.alerted_content,
        EVENT_ACTIONS.clicked,
        "Alert Content - Map Pin"
      );

      return this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
    mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      this.selectedClusterFeatures = features;
      this.displayResults(features);
      console.log("map click event");
    },
    clusterMapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      this.selectedClusterFeatures = features;
      this.displayResults(features);
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
  },
  watch: {
    displayWizard() {
      this.currentView = this.displayWizard
        ? this.viewDetails.wizard.id
        : this.viewDetails.myAlerts.id;
    },
    searchDetails: {
      async handler() {
        if (this.searchType === "alerts") {
          if (this.searchDetails) {
            let res = this.selectedClusterFeatures.filter((item) => {
              if (
                (
                  item.properties.formatted.title.toLowerCase() ||
                  item.properties.formatted.source.toLowerCase() ||
                  item.properties.formatted.summary.toLowerCase()
                ).includes(this.searchDetails.toLowerCase())
              ) {
                return item;
              }
            });
            this.displayResults(res);
          } else {
            this.displayResults(this.selectedClusterFeatures);
          }
        }
      },
    },
    refreshMap: {
      async handler() {
        if (this.refreshMap) {
          if (
            this.preferences &&
            this.preferences.alerts &&
            Array.isArray(this.preferences.alerts.mapped) &&
            this.preferences.alerts.mapped.length
          ) {
            this.getData({ alert: this.preferences.alerts.mapped });
            this.mapped = this.preferences.alerts.mapped;
          }
        }
        this.$store.dispatch(
          MODULE_PREFIX_ALERTS_CONTENT + GET_ALERTS_REFRESH_MAP,
          false
        );
      },
    },
  },
};
</script>

<style scoped>
.alerts-container {
  height: 95vh !important;
  padding: 0 !important;
}
</style>
